
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import FileDropzone from "./FileDropzone";
import TextInput from "./TextInput";
import AnalysisProgress from "./AnalysisProgress";
import AnalysisTip from "./AnalysisTip";

export interface AnalysisUploaderProps {
  title: string;
  description: string;
  acceptedFileTypes: string;
  icon: React.ReactNode;
  onAnalyze: (file: File | string) => Promise<void>;
  allowText?: boolean;
  isLoading?: boolean;
  progress?: number;
  analysisStatus?: 'idle' | 'processing' | 'completed' | 'error';
  maxSize?: number;
}

const AnalysisUploader = ({
  title,
  description,
  acceptedFileTypes,
  icon,
  onAnalyze,
  allowText = false,
  isLoading = false,
  progress = 0,
  analysisStatus = 'idle',
  maxSize = 5 * 1024 * 1024, // Default to 5MB
}: AnalysisUploaderProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAnalysis = async () => {
    try {
      if (file) {
        console.log("Analyzing file:", file.name);
        await onAnalyze(file);
      } else if (text) {
        console.log("Analyzing text of length:", text.length);
        await onAnalyze(text);
      } else {
        toast.error("Please provide content to analyze");
        return;
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Analysis failed. Please try again.");
    }
  };

  const isProcessing = analysisStatus === 'processing' || isLoading;
  
  // Calculate progress display - ensuring we show a minimum value during processing
  const displayProgress = isProcessing ? Math.max(30, progress) : progress;

  return (
    <Card className="w-full shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!file && !isProcessing && allowText && (
          <TextInput text={text} setText={(value) => {
            setText(value);
            setFile(null);
          }} />
        )}
        
        {!text && !isProcessing && (
          <FileDropzone 
            file={file} 
            setFile={setFile}
            acceptedFileTypes={acceptedFileTypes}
            onClearFile={handleClearFile}
            maxSize={maxSize}
          />
        )}
        
        {isProcessing && (
          <AnalysisProgress 
            progress={displayProgress} 
            isAnalyzing={isProcessing} 
            status={analysisStatus}
          />
        )}
        
        {!file && !text && !isProcessing && (
          <AnalysisTip
            title="Tip"
            description={`For best results, ensure your ${title.toLowerCase()} is clear and high quality.`}
          />
        )}
      </CardContent>
      <CardFooter>
        <Button
          onClick={handleAnalysis}
          disabled={isProcessing || (!file && !text)}
          className="w-full bg-fraudeye-accent hover:bg-fraudeye-highlight"
          type="button"
        >
          {isProcessing ? "Analyzing..." : "Start Analysis"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AnalysisUploader;
